import { useEffect, useRef, useState } from 'react';
import { PageHeading } from '@portal_components/components/PageHeading';
import { EExceptionOverviewMode, ExceptionOverviewForm, HandoverExceptionOverview, HandoverExceptionOverviewsPage } from '../types';
import { DateTime } from 'luxon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { EHttpMethod, usePortalQuery } from '../hooks/usePortalQuery';
import { FormProvider, useForm } from 'react-hook-form';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { FormButton } from '@portal_components/components/FormButton';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { Button } from '@portal_components/components/Button';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

export const ExceptionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [isRequestingMore, setIsRequestingMore] = useState(true);
  const [exceptionsOverviews, setExceptionsOverviews] = useState<HandoverExceptionOverview[]>([]);
  const [nextStartKey, setNextStartKey] = useState<string | undefined>(undefined);

  const validationSchema = Yup.object<ExceptionOverviewForm>({
    mode: Yup.string<EExceptionOverviewMode>().oneOf(Object.values(EExceptionOverviewMode), 'Invalid mode selected'),
    searchTerm: Yup.string().when(['mode'], ([mode], schema) => {
      return mode === EExceptionOverviewMode.MPAN
        ? schema.required().matches(/[0-9]{13}/g, 'Mpan is invalid, it must contain 13 digits')
        : schema.required().required('Handover File Name is required');
    })
  });

  const methods = useForm<ExceptionOverviewForm>({
    defaultValues: {
      searchTerm: searchParams.get('searchTerm') ?? searchParams.get('partialHandoverFileName') ?? '',
      mode: Object.values(EExceptionOverviewMode).includes(searchParams.get('mode') as EExceptionOverviewMode) ? (searchParams.get('mode') as EExceptionOverviewMode) : EExceptionOverviewMode.MPAN
    },
    resolver: yupResolver<ExceptionOverviewForm>(validationSchema as any)
  });
  const searchMode = methods.watch('mode');
  useEffect(() => {
    methods.setValue('searchTerm', '');
  }, [methods, searchMode]);

  const handleSearch = (data: ExceptionOverviewForm) => {
    setNextStartKey(undefined);
    setSearchParams({
      searchTerm: data.searchTerm ?? '',
      mode: data.mode ?? ''
    });
    setExceptionsOverviews([]);
    setIsRequestingMore(true);
  };

  const handleReset = () => {
    setNextStartKey(undefined);
    setSearchParams({});
    methods.setValue('searchTerm', '');
    setExceptionsOverviews([]);
    setIsRequestingMore(true);
  };

  usePortalQuery<HandoverExceptionOverviewsPage>(
    EHttpMethod.GET,
    `exceptions-overview?${nextStartKey ? `startKey=${nextStartKey}` : ''}&${searchParams}`,
    `get-exceptions-overview-${nextStartKey ?? 'initial'}`,
    isRequestingMore,
    undefined,
    data => {
      setExceptionsOverviews(curr => [...curr, ...data.exceptions]);
      setNextStartKey(data.startKey);
      setIsRequestingMore(false);
    }
  );

  const loadMoreExceptions = async () => {
    if (isRequestingMore) return;
    if (!nextStartKey) return;
    setIsRequestingMore(true);
  };

  const handleExceptionClick = (handoverFileName: string) => () => {
    navigate(`/exceptions/${handoverFileName}`);
  };

  const handleNoAppointmentExceptionClick = (handoverFileName: string) => () => {
    navigate(`/no-appointment-exceptions/${handoverFileName}`);
  };

  const renderExceptionOverview = (_index: number, exception: HandoverExceptionOverview) => {
    return (
      <div className="mb-6 mr-2 rounded-l-sm border-l-[4px] border-interactive-coral bg-elevation-1 ">
        <div className="flex flex-col w-full gap-1 px-4 pt-2 pb-3" key={exception.handoverFileName}>
          <p className="text-xl">
            {exception.customerGroupName} - {exception.handoverFileName}
          </p>
          <div className="w-full flex flex-row">
            <p>Contract: {exception.contractId}</p>
            <p className="ml-auto">Uploaded Date: {DateTime.fromISO(exception.uploadDate).toFormat('dd/MM/yyyy')}</p>
          </div>
        </div>
        <div className="flex flex-row w-full gap-[2px]">
          <div className="w-1/2 bg-elevation-2 hover:bg-elevation-3 py-1.5 cursor-pointer flex justify-center items-center" onClick={handleNoAppointmentExceptionClick(exception.handoverFileName)}>
            <p>View {exception.numberOfNoAppointmentExceptions} Unappointed MPANs</p>
            <img src="/attention.svg" alt="Unappointed MPANs" className="inline-block ml-2 h-5 w-5" />
          </div>
          <div className="w-1/2 bg-elevation-2 hover:bg-elevation-3 py-1.5 cursor-pointer flex justify-center items-center" onClick={handleExceptionClick(exception.handoverFileName)}>
            <p>View {exception.numberOfExceptions} Exceptions</p>
            <img src="/attention.svg" alt="Unappointed MPANs" className="inline-block ml-2 h-5 w-5" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      <PageHeading
        title="Exceptions"
        navigationButtons={[
          {
            title: 'Back',
            path: '/',
            icon: '/back.svg',
            className: 'w-6 h-6'
          }
        ]}
      />
      <div className="mb-4">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSearch)}>
            <FormRadioButtonGroup
              title="Search Exceptions By"
              name="mode"
              buttons={[
                { title: 'MPAN', value: EExceptionOverviewMode.MPAN },
                { title: 'Handover File Name', value: EExceptionOverviewMode.HANDOVER_FILE_NAME }
              ]}
            />
            <div className="flex flex-row">
              <FormTextInput title={searchMode === EExceptionOverviewMode.MPAN ? 'MPAN' : 'Handover File Name'} name="searchTerm" />
              <div className="pl-2 pt-2 pb-4 mb-2">
                <FormButton type="submit" primary>
                  Search
                </FormButton>
              </div>
              <div className="pl-2 pt-2 pb-4 mb-2">
                <Button text="Reset" primary={false} onClick={handleReset} />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
      <Virtuoso
        ref={virtuosoRef}
        data={exceptionsOverviews}
        itemContent={renderExceptionOverview}
        firstItemIndex={0}
        endReached={loadMoreExceptions}
        useWindowScroll
        style={{ paddingBottom: '5rem' }}
        components={{
          Footer: () => {
            if (!exceptionsOverviews.length && !isRequestingMore && !nextStartKey) {
              return (
                <div className="flex justify-center items-center h-12">
                  <p>No exceptions found.</p>
                </div>
              );
            }
            if (!isRequestingMore) return <div style={{ height: '5rem' }} />;
            return (
              <div className="flex justify-center items-center h-12">
                <p>Loading more exceptions...</p>
              </div>
            );
          }
        }}
      />
    </div>
  );
};
